import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, Headset, Sparkles } from 'lucide-react';
import { OptiVeloLogo } from './OptiVeloLogo';
import { markChecklistItemCompleted } from '../src/lib/checklist';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
}

function getFallbackChatResponse(messageText: string): string {
  const norm = messageText.toLowerCase().trim();
  
  // 1. Nicho / Segmento / Tipo de Produto / Quem serve / Funciona para
  if (
    norm.includes("nicho") || 
    norm.includes("segmento") || 
    norm.includes("tipo de produto") ||
    norm.includes("funciona para") ||
    norm.includes("serve para") ||
    norm.includes("vender") ||
    norm.includes("meu produto") ||
    norm.includes("infoproduto") ||
    norm.includes("e-commerce") ||
    norm.includes("dropshipping") ||
    norm.includes("serviço") ||
    norm.includes("negócio local")
  ) {
    return "Sim! O **OptiVelo serve perfeitamente para qualquer nicho de mercado**!\n\nSeja você um criador de infoprodutos (cursos, e-books, mentorias), gestor de e-commerce ou dropshipping, prestador de serviços, agência de tráfego, proprietário de SaaS ou profissional autônomo (médicos, advogados, consultores), nossa plataforma e nossa Inteligência Artificial foram calibradas para estruturar a linguagem exata do seu público-alvo.\n\nA IA do OptiVelo analisa o perfil, ajusta os gatilhos mentais e otimiza a conversão de acordo com as especificidades do seu nicho para atrair cliques qualificados e conversões rápidas. Quer testar de graça por 14 dias e ver o resultado no seu nicho?";
  }

  // 2. Proteção de Propriedade Intelectual / IP / Cópia / Segurança / Terceiros / Agência
  if (
    norm.includes("copiar") ||
    norm.includes("roubar") ||
    norm.includes("segurança") ||
    norm.includes("propriedade intelectual") ||
    norm.includes("proteção") ||
    norm.includes("proteger") ||
    norm.includes("terceiros") ||
    norm.includes("agência") ||
    norm.includes("segredo") ||
    norm.includes("acesso")
  ) {
    return "A segurança dos seus projetos e da sua **Propriedade Intelectual (IP)** é prioridade máxima no OptiVelo!\n\nDisponibilizamos um **Guia Completo de Segurança e Proteção de IP** diretamente no Painel de Controle (aba 'Guia de Lançamento'). Lá indicamos as melhores práticas contratuaise e de acessos ao compartilhar o painel com freelancers ou agências:\n\n1. **Controle de Acesso**: Nunca entregue sua conta master. Crie credenciais limitadas se necessário.\n2. **Segurança de APIs**: Todas as chaves do Google Gemini e acessos ao banco de dados Firestore rodam de forma privada por trás do nosso servidor robusto, evitando vazamento ou roubo de código.\n3. **Cláusulas de NDAs**: Damos orientações de como firmar acordos de confidencialidade com desenvolvedores terceiros.\n\nVocê pode conferir esses insights de segurança imediatamente acessando seu painel!";
  }

  // 3. Preços / Planos / Valores / Custos / Assinatura
  if (
    norm.includes("preço") || 
    norm.includes("plano") || 
    norm.includes("pagar") || 
    norm.includes("valor") || 
    norm.includes("custo") ||
    norm.includes("assinar") ||
    norm.includes("comprar") ||
    norm.includes("mensalidade")
  ) {
    return "Atualmente, oferecemos três planos principais na OptiVelo, com valores promocionais ativos de Membros Fundadores:\n\n1. **Plano Pro**: R$ 197/mês (Membro Fundador) ou R$ 297/mês tradicional. Ideal para iniciantes dominarem as primeiras páginas.\n2. **Plano Scale**: R$ 347/mês (Membro Fundador) ou R$ 497/mês tradicional. Perfeito para operações estabilizadas que escalam tráfego e múltiplos domínios.\n3. **Plano Enterprise**: R$ 647/mês (Membro Fundador) ou R$ 997/mês tradicional. Desenhado para agências, copys profissionais e alto volume corporal.\n\nTodos os planos incluem **período experimental gratuito de 14 dias**! Você não paga nada para iniciar a otimização hoje. Qual plano faz mais sentido para você agora?";
  }
  
  // 4. Teste Grátis / Trial / Gratuito
  if (
    norm.includes("teste") || 
    norm.includes("grátis") || 
    norm.includes("gratis") || 
    norm.includes("trial") || 
    norm.includes("experimentar") ||
    norm.includes("de graça")
  ) {
    return "Com certeza! Você pode iniciar seu teste **100% gratuito de 14 dias** em qualquer plano do OptiVelo.\n\nDurante esse período experimental, o sistema fica integralmente destravado: você pode realizar diagnósticos de velocidade, usar o gerador de copy inteligente por inteligência artificial, conectar domínios com HTTPS automático e começar a coletar leads. Não existem taxas surpresas e você pode cancelar com um único clique no painel se mudar de ideia. Gostaria de me ajudar a te direcionar para a tela de ativação?";
  }

  // 5. Como funciona / O que faz / O que é / Usabilidade
  if (
    norm.includes("como funciona") || 
    norm.includes("como funcionam") || 
    norm.includes("como usar") || 
    norm.includes("funciona") || 
    norm.includes("funcionamento") ||
    norm.includes("o que é") ||
    norm.includes("o que e") ||
    norm.includes("o que faz")
  ) {
    return "O **OptiVelo** é um ecossistema inteligente de alta performance para conversão de vendas em landing pages!\n\nEle funciona através de 4 pilares centrais:\n\n1. **Diagnóstico Cuidadoso**: Analisa a velocidade da sua página carregamento, scripts pesados e boas práticas de compliance legal.\n2. **Brain IA Copywriter**: Produz headlines magnéticas, copies de páginas inteiras e anúncios de tráfego baseados em frameworks de conversão validados no mercado brasileiro.\n3. **Domínios Rápidos**: Conecte seus domínios personalizados com protocolo HTTPS de graça em menos de 2 minutos.\n4. **Captura Automatizada**: Integração para gerenciar leads diretamente ou exportar para canais externos.\n\nÉ a junção perfeita de velocidade extrema com persuasão textual. Quer fazer um teste rápido e ver as páginas carregando instantaneamente?";
  }

  // 6. Integrações / ActiveCampaign / Webhook / Pixel / Leads
  if (
    norm.includes("integrar") || 
    norm.includes("integração") || 
    norm.includes("integracao") || 
    norm.includes("activecampaign") || 
    norm.includes("webhook") || 
    norm.includes("api") ||
    norm.includes("pixel") ||
    norm.includes("lead")
  ) {
    return "O OptiVelo possui conexões robustas e automáticas para coletar seus leads:\n\n- **ActiveCampaign**: Conecte suas listas em menos de 10 segundos enviando apenas sua API Key oficial.\n- **Webhooks Globais**: Dispare as informações dos leads em formato JSON para qualquer central externa como Zapier, Make, n8n ou vendedoras de CRM.\n- **Pixels Próprios**: Insira códigos de rastreamento do Pixel do Facebook, TikTok, e Google Analytics de forma visual.\n\nVocê configura tudo direto na aba **'Integrações'** no painel de administração lateral!";
  }

  // 7. Domínio / DNS / CNAME / Endereço / Hospedagem
  if (
    norm.includes("domínio") || 
    norm.includes("dominio") || 
    norm.includes("dns") || 
    norm.includes("cname") || 
    norm.includes("apontar") || 
    norm.includes("registro") ||
    norm.includes("hospedagem")
  ) {
    return "Apontar seu domínio personalizado com o OptiVelo é simples e imediato:\n\n1. Acesse o menu **'Domínios'** no painel do administrador.\n2. Escreva o seu domínio ou subdomínio (ex: `vendas.meusite.com.br`).\n3. Adicione um registro DNS da sua hospedagem (Cloudflare, Registro.br, Hostgator, etc.):\n   - Registro **A** apontando para o IP indicado em nosso painel.\n   - Ou Registro **CNAME** se for um subdomínio específico.\n\nNosso servidor identifica o apontamento, emite e estende o certificado SSL (HTTPS de cadeado verde) com criptografia militar de forma **100% gratuita** e automática!";
  }

  // 8. Velocidade / Lentidão / Mobile / Carregar / Tempo de resposta
  if (
    norm.includes("velocidade") || 
    norm.includes("lento") || 
    norm.includes("rápido") || 
    norm.includes("rapido") || 
    norm.includes("carregar") || 
    norm.includes("carregamento") ||
    norm.includes("mobile") ||
    norm.includes("celular")
  ) {
    return "A velocidade de carregamento é o fator nº 1 de perda de vendas no tráfego pago atualmente. Sabia que páginas que demoram mais de 3 segundos para carregar chegam a perder até 53% dos visitantes?\n\nO OptiVelo resolve isso de duas formas:\n1. **Otimizador**: Nosso diagnóstico detecta imagens pesadas, scripts redundantes de rastreamento e otimiza a página para atingir notas máximas de performance.\n2. **Mobile Clean**: Garante compressão extrema de recursos visuais para que celulares em conexões 3G/4G acessem sua oferta em menos de 1 segundo.\n\nQueremos que sua publicidade custe menos e converta muito mais!";
  }

  // 9. Ajuda técnica / Suporte / Erros / Denúncia / Suporte Humano
  if (
    norm.includes("ajuda") || 
    norm.includes("suporte") || 
    norm.includes("erro") || 
    norm.includes("problema") || 
    norm.includes("bug") || 
    norm.includes("não funciona") || 
    norm.includes("falha") ||
    norm.includes("humano") ||
    norm.includes("atendente")
  ) {
    return "Olá! Eu sou o assistente virtual do OptiVelo. Se você encontrou alguma dificuldade operacional em sua conta de testes, deparou-se com algum comportamento indesejado, ou precisa de esclarecimento técnico profundo, posso colocar você em contato direto com nosso suporte humano especializado.\n\nGostaria que eu abrisse um ticket ou transferisse esta conversa para nossa central interna?";
  }

  // Fallback padrão amigável extremamente robusto e comercial
  return "Compreendo perfeitamente sua mensagem! Como Consultor de Inteligência do OptiVelo, estou aqui para impulsionar seu sucesso comercial. \n\nPosso te orientar nos seguintes tópicos fundamentais:\n- **Nicho de Mercado**: Como a IA otimiza páginas para o seu segmento específico.\n- **Proteção Intelectual**: Como manter o código e chaves protegidos contra cópias de terceiros.\n- **Planos e Preços**: Detalhes do nosso desconto de Membros Fundadores.\n- **Como Funciona**: Guia passo a passo de velocidade e copy.\n- **Domínios & DNS**: Configuração do HTTPS em poucos instantes.\n\nQual dessas frentes você gostaria de explorar hoje?";
}

export const PublicChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Tooltip is now persistent when chat is closed
  useEffect(() => {
    // No longer hiding tooltip automatically or on open
  }, [isOpen]);

  const handleSend = async (overrideMessage?: string) => {
    const messageText = overrideMessage || input;
    if (!messageText.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    markChecklistItemCompleted('support');

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: messages.map(m => ({ role: m.role, text: m.text })),
          messageText
        })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || contentType.includes('text/html')) {
        console.warn("API returned error or HTML index.html (static routing). Running client-side chat fallback...");
        const fullText = getFallbackChatResponse(messageText);
        setMessages(prev => [...prev, {
          role: 'model',
          text: fullText,
          timestamp: new Date()
        }]);
        return;
      }

      const data = await response.json();
      const fullText = data.text || "";

      setMessages(prev => [...prev, {
        role: 'model',
        text: fullText,
        timestamp: new Date()
      }]);
    } catch (error: any) {
      console.warn("Public Chat Error, using local fallback assistant:", error);
      const fullText = getFallbackChatResponse(messageText);
      setMessages(prev => [...prev, {
        role: 'model',
        text: fullText,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-4">
      {/* Tooltip Message */}
      {showTooltip && !isOpen && (
        <div className="bg-white text-slate-900 px-3 py-2 rounded-xl shadow-2xl border border-indigo-100 animate-bounce-subtle relative max-w-[180px] group/tooltip">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(false);
            }}
            className="absolute -top-2 -left-2 w-5 h-5 bg-white border border-indigo-100 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-500 hover:scale-110 transition-all shadow-sm opacity-0 group-hover/tooltip:opacity-100"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="text-[10px] font-bold leading-tight">
            Dúvidas? Clique aqui! 🚀
          </p>
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-indigo-100 rotate-45"></div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen ? (
        <div className="w-[350px] sm:w-[400px] h-[550px] bg-[#0b0e14] border border-white/10 rounded-[32px] shadow-2xl flex flex-col animate-in slide-in-from-bottom-4 duration-300 overflow-hidden">
          {/* Header */}
          <div className="p-5 border-b border-white/5 flex items-center justify-between bg-indigo-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center text-white shadow-lg shadow-indigo-500/10">
                <OptiVeloLogo size={24} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Consultor OptiVelo</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">IA Especialista Online</span>
                </div>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-hide"
          >
            {messages.length === 0 && (
              <div className="text-center py-10 space-y-6">
                <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto text-indigo-500">
                  <Sparkles className="w-8 h-8" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-white font-bold">Olá! Bem-vindo ao OptiVelo.</p>
                  <p className="text-xs text-slate-400 px-6 leading-relaxed">
                    Eu sou o seu consultor inteligente. Como posso ajudar você a transformar sua estratégia de tráfego em lucro hoje?
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 px-4">
                  {['Como funciona?', 'Quais os preços?', 'Tem teste grátis?'].map((q) => (
                    <button 
                      key={q}
                      onClick={() => handleSend(q)}
                      className="text-[10px] font-bold uppercase tracking-wider px-3 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 rounded-lg text-slate-300 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user' 
                    ? 'bg-indigo-500 text-white rounded-tr-none shadow-lg shadow-indigo-500/10' 
                    : 'bg-white/5 text-slate-300 rounded-tl-none border border-white/5'
                }`}>
                  <div className="markdown-body">
                    <ReactMarkdown>{m.text}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 p-4 rounded-2xl rounded-tl-none border border-white/5 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-5 border-t border-white/5 bg-white/[0.01]">
            <div className="relative">
              <input 
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Pergunte qualquer coisa..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-5 pr-14 py-4 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all placeholder:text-slate-600"
              />
              <button 
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-indigo-500 text-white rounded-xl hover:bg-indigo-400 transition-all disabled:opacity-50 shadow-lg shadow-indigo-500/20"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-2 mt-4 opacity-30">
              <div className="h-[1px] w-8 bg-slate-500"></div>
              <p className="text-[8px] text-slate-500 font-black uppercase tracking-[0.2em]">
                OptiVelo Intelligence
              </p>
              <div className="h-[1px] w-8 bg-slate-500"></div>
            </div>
          </div>
        </div>
      ) : (
        /* Floating Button */
        <button 
          onClick={() => setIsOpen(true)}
          className="w-12 h-12 bg-indigo-500 text-white rounded-xl shadow-2xl shadow-indigo-500/40 flex items-center justify-center hover:scale-110 hover:-rotate-3 transition-all z-50 group relative"
        >
          <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-[#0b0e14] rounded-full"></div>
        </button>
      )}
    </div>
  );
};
